/**
 * Запросы `GET /analytics/voice` для вкладок раздела «Голос клиента» (#848) и
 * окно потока, по которому они идут.
 *
 * - `useVoiceOfCustomer` — весь раздел: «Маркетинг» и «Продукт и сервис».
 *   Один ключ запроса: смена вкладки при том же фильтре берёт уже полученный
 *   ответ, а не сканирует заново. Блоков вопросов в нём нет — полный ответ
 *   кешируется (code-review #1802).
 * - `useVoiceCustomerQuestions` — только блоки вопросов и отказов
 *   (`only=customerQuestions`) для «Продаж» и «Сайта и контента»: вкладкам
 *   нужен один ключ, а не сканы раздела.
 * - `useVoiceThemeAnalytics` — только темы разговоров (`only=themes`, #845) на
 *   тех же вкладках: свой ключ на каждый срез, ответ не кешируется.
 *
 * Фильтр темы (`voiceTheme`, #2229, спека 2026-10-11-voice-report-layers.md
 * §3) уходит параметром `themeCode` во все запросы раздела, кроме блока тем:
 * он читает набор целиком. Код темы стоит в ключе ПЕРЕД видом — вид остаётся
 * последним элементом (`voiceThemeAnalyticsFilter`); прежний ответ под новой
 * темой не держится: его числа — другой темы (или всего потока).
 */
import type { VoiceThemeCuts } from '@aica/api-types/schemas';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  type QueryFilters,
} from '@tanstack/react-query';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import { useLockedDemoAsked } from '@/contexts/LockedDemoContext';
import { sortedProjectIdsKey } from '@/lib/project-filter';
import { retryTransientOnly } from '@/lib/query-retry';
import { useStreamWindow } from '../stream-filter/useStreamWindow';
import { useStreamDemoFilter } from '../stream-filter/useStreamDemoFilter';
import { objectionStreamRequest } from '../objections/useObjectionStream';
import { keepsThemePlaceholder, themeFilteredQueries } from './theme-filter-key';
import type { VoiceAudience, VoiceSearch } from './voice-search';

/** The themes cut each tab reads (`undefined` — the rows alone, no cuts). */
const THEME_CUT_OF_AUDIENCE: Record<VoiceAudience, VoiceThemeCuts | undefined> = {
  sales: 'outcomes',
  site: 'questions',
  marketing: undefined,
  product: undefined,
};

/**
 * The kind element of a themes-block query key: `themes-<cut>`, or
 * `themes-none` for the block without cuts (the theme picker on Marketing and
 * Product and service needs only the rows).
 */
const THEMES_KIND_PREFIX = 'themes-';

/** Where the theme sits in a voice key: just before the kind, the last element. */
const THEME_FROM_END = 2;
// The demo mode, right before the theme.
const MODE_FROM_END = 3;

const keepsPlaceholder = (
  previousKey: readonly unknown[] | undefined,
  key: readonly unknown[]
): boolean => keepsThemePlaceholder(previousKey, key, THEME_FROM_END, MODE_FROM_END);

type VoiceRequestKind =
  | 'section'
  | 'questions'
  | 'site-questions'
  | 'themes-outcomes'
  | 'themes-questions'
  | 'themes-none';

/**
 * Window, scope and demo rule of a voice request — what does not depend on the
 * kind. The demo rule is the section's (`useStreamDemoFilter`): the monthly
 * email's exact window counts without demo calls on every tab, the Objections
 * screen included; otherwise the organization's policy decides. A hook, so it
 * runs once per component, however many kinds the component asks for
 * (`buildVoiceRequest` is the plain part).
 */
export function useVoiceRequestBase(
  search: VoiceSearch,
  onSearchChange: (patch: Partial<VoiceSearch>) => void
) {
  const { apiClient, orgId } = useAuth();
  const { includeDemo, ready: demoReady } = useStreamDemoFilter(search, 'voice-section');
  const stream = useStreamWindow(search, onSearchChange);
  // The demo of a closed section and the organization's own answer are
  // different answers (#2816): after an upgrade the cached demo must not be
  // drawn as the customer's own data.
  const lockedDemo = useLockedDemoAsked();
  return {
    apiClient,
    orgId,
    stream,
    includeDemo,
    lockedDemo,
    demoReady,
    category: search.category,
    voiceTheme: search.voiceTheme,
  };
}

export type VoiceRequestBase = ReturnType<typeof useVoiceRequestBase>;

/** Query key and parameters of one kind of voice request — no hooks. */
function buildVoiceRequest(base: VoiceRequestBase, kind: VoiceRequestKind) {
  const { apiClient, orgId, stream, includeDemo, lockedDemo, demoReady } = base;
  const { dateRange, projectIds, callCategory } = stream;
  // The themes block is never narrowed by the filter (§2.5): no theme in its
  // key or request — `only=themes` with `themeCode` is a 400.
  const themeCode = kind.startsWith(THEMES_KIND_PREFIX) ? undefined : base.voiceTheme;
  return {
    apiClient,
    orgId,
    stream,
    queryKey: [
      'analytics',
      'voice',
      orgId,
      dateRange.periodStart,
      dateRange.periodEnd,
      sortedProjectIdsKey(projectIds),
      callCategory,
      includeDemo,
      lockedDemo ? 'demo' : 'own',
      themeCode ?? null,
      kind,
    ] as const,
    params: {
      periodStart: dateRange.periodStart,
      periodEnd: dateRange.periodEnd,
      projectIds,
      callCategory: base.category,
      includeDemo,
      ...(themeCode ? { themeCode } : {}),
    },
    // Asked as the demo: the browser cache is bypassed (`ReportsApi`).
    lockedDemo,
    enabled: Boolean(orgId) && demoReady,
  };
}

function useVoiceRequest(
  search: VoiceSearch,
  onSearchChange: (patch: Partial<VoiceSearch>) => void,
  kind: VoiceRequestKind
) {
  return buildVoiceRequest(useVoiceRequestBase(search, onSearchChange), kind);
}

type VoiceRequest = ReturnType<typeof buildVoiceRequest>;

/** The kind of a themes-block request for a tab's cut. */
const themesKind = (cut: VoiceThemeCuts | undefined) =>
  `${THEMES_KIND_PREFIX}${cut ?? 'none'}` as VoiceRequestKind;

/**
 * What the reads add when they ask the demo of a closed section (#2816): the
 * browser cache is bypassed (`ReportsApi`). The own ask is the call as before.
 */
export const demoAsk = (request: { lockedDemo: boolean }): [{ lockedDemo: true }] | [] =>
  request.lockedDemo ? [{ lockedDemo: true }] : [];

/** The whole section's request — one for the hook and the prefetch. */
const fetchSection = (request: VoiceRequest) => () =>
  request.apiClient.reports.getVoiceOfCustomer(request.params, ...demoAsk(request));

/**
 * The customer-questions blocks' request. Site draws no declines: it asks
 * without their breakdowns and conversations (employee names, wordings,
 * records — #1641).
 */
const fetchQuestions = (request: VoiceRequest, site: boolean) => () =>
  request.apiClient.reports.getVoiceCustomerQuestions(
    site ? { ...request.params, breakdowns: 'none' } : request.params,
    ...demoAsk(request)
  );

/** The themes block's request — one for the hook and the prefetch. */
const fetchThemes = (request: VoiceRequest, cut: VoiceThemeCuts | undefined) => () =>
  request.apiClient.reports.getVoiceThemes(
    {
      ...request.params,
      ...(cut ? { themeCuts: cut } : {}),
    },
    ...demoAsk(request)
  );

/** The whole section — the marketing and product tabs. */
export function useVoiceOfCustomer(
  search: VoiceSearch,
  onSearchChange: (patch: Partial<VoiceSearch>) => void
) {
  const request = useVoiceRequest(search, onSearchChange, 'section');
  const query = useQuery({
    queryKey: request.queryKey,
    queryFn: fetchSection(request),
    enabled: request.enabled,
    // Keep the previous numbers while a filter changes — never across
    // organizations: that would show one tenant's customer words under
    // another's name until the new request lands.
    placeholderData: (previous, previousQuery) =>
      keepsPlaceholder(previousQuery?.queryKey, request.queryKey)
        ? keepPreviousData(previous)
        : undefined,
    retry: retryTransientOnly,
  });
  // The FAQ draft (#843) is built for exactly this window and scope: it takes
  // the section's parameters and key rather than resolving its own window.
  return {
    query,
    stream: request.stream,
    request: { params: request.params, key: request.queryKey },
  };
}

/**
 * The customer-questions blocks (`only=customerQuestions`), on Sales and on
 * Site and content, by the section's demo rule (`useVoiceRequest`).
 *
 * - Sales (the default): no previous numbers under a new filter — the
 *   summary cards and sections say they are loading rather than show the
 *   previous filter's numbers as current.
 * - Site and content (`site: true`): its own key and a read without
 *   anything behind the declines it does not draw (`breakdowns=none`); the tab has its own filter bar, so
 *   it keeps the previous numbers while a filter changes — never across
 *   organizations.
 */
export function useVoiceCustomerQuestions(
  search: VoiceSearch,
  onSearchChange: (patch: Partial<VoiceSearch>) => void,
  options: { site?: boolean; enabled?: boolean } = {}
) {
  const site = options.site === true;
  const request = useVoiceRequest(
    search,
    onSearchChange,
    site ? 'site-questions' : 'questions'
  );
  const query = useQuery({
    queryKey: request.queryKey,
    queryFn: fetchQuestions(request, site),
    // `enabled: false` — the blocks' flag is closed: no request at all.
    enabled: request.enabled && options.enabled !== false,
    // Site only: Sales shows no previous numbers under a new filter.
    placeholderData: (previous, previousQuery) =>
      site && keepsPlaceholder(previousQuery?.queryKey, request.queryKey)
        ? keepPreviousData(previous)
        : undefined,
    retry: retryTransientOnly,
  });
  // The FAQ draft (#843) is built for exactly this window and scope.
  return {
    query,
    stream: request.stream,
    request: { params: request.params, key: request.queryKey },
  };
}

/**
 * The Sales tab's objections stream under the section's request base — its own
 * key family (`objection-stream`), the tab's own parameters (the section's
 * window, its theme filter, the server's marks). One for the tab prefetch and
 * the demo banner; `null` until there is an organization and the demo rule.
 */
function salesStreamRequest(base: VoiceRequestBase) {
  if (!base.orgId || !base.demoReady) return null;
  return objectionStreamRequest({
    apiClient: base.apiClient,
    orgId: base.orgId,
    stream: base.stream,
    includeDemo: base.includeDemo,
    lockedDemo: base.lockedDemo,
    category: base.category,
    themeCode: base.voiceTheme,
    marks: true,
  });
}

/**
 * Warms what a tab asks for after the click (#2316): called on intent — the
 * pointer resting on its trigger, or focus on it. An inactive tab is unmounted,
 * so its first request used to start only after the switch — the whole section
 * took ~4 s at the largest organization. Same key and request as the tab's own
 * hooks, so the tab takes the warmed answer; `prefetchQuery` does not refetch a
 * fresh one.
 *
 * - Marketing and Product: the whole section, and the theme picker's rows.
 * - Sales and Site: the customer-questions blocks — only while the blocks'
 *   switch lets the tab ask (`questionsOn`) — and the themes block of the
 *   tab's cut (`outcomes`, `questions`).
 *
 * The themes request goes only where the tab sends it: the flag
 * `enableConversationThemes` (`themesOn`). Never a request the tab itself would
 * not send. Sales also warms its objections stream (`objectionStreamRequest`,
 * the tab's own key): a section opened on another tab — the monthly email's
 * link, an address — has never asked for it.
 */
export function useVoiceTabPrefetch(
  search: VoiceSearch,
  onSearchChange: (patch: Partial<VoiceSearch>) => void,
  { questionsOn, themesOn }: { questionsOn: boolean; themesOn: boolean }
): (audience: VoiceAudience) => void {
  const queryClient = useQueryClient();
  const base = useVoiceRequestBase(search, onSearchChange);
  const warm = <T>(
    kind: VoiceRequestKind,
    queryFn: (request: VoiceRequest) => () => Promise<T>
  ) => {
    const request = buildVoiceRequest(base, kind);
    if (!request.enabled) return;
    void queryClient.prefetchQuery({
      queryKey: request.queryKey,
      queryFn: queryFn(request),
      retry: retryTransientOnly,
    });
  };
  return (audience: VoiceAudience) => {
    const cut = THEME_CUT_OF_AUDIENCE[audience];
    if (audience === 'sales' || audience === 'site') {
      const site = audience === 'site';
      if (questionsOn) {
        warm(site ? 'site-questions' : 'questions', (request) =>
          fetchQuestions(request, site)
        );
      }
      const stream = site ? null : salesStreamRequest(base);
      if (stream) {
        void queryClient.prefetchQuery({
          queryKey: stream.queryKey,
          queryFn: stream.queryFn,
          retry: retryTransientOnly,
        });
      }
    } else {
      warm('section', fetchSection);
    }
    if (themesOn) warm(themesKind(cut), (request) => fetchThemes(request, cut));
  };
}

/** The requests whose answers a tab draws its numbers from. */
const KINDS_OF_AUDIENCE: Record<VoiceAudience, readonly VoiceRequestKind[]> = {
  sales: ['questions', 'themes-outcomes'],
  site: ['site-questions', 'themes-questions'],
  marketing: ['section'],
  product: ['section'],
};

/**
 * The query keys of the open tab's requests under the section's CURRENT window,
 * scope and filter — exactly what the tab's own hooks ask now. Nothing from an
 * earlier tab, period or filter: their answers sit under other keys.
 *
 * - `blocks` — the tab's section/questions/themes requests;
 * - `stream` — the Sales tab's objections stream, kept apart: it is open to
 *   every package and outside the demo window returns the organization's real
 *   rows, so only its own `demoOnly: true` marks it as the demo, and a refused
 *   block still outweighs it.
 */
export function useVoiceTabQueryKeys(
  search: VoiceSearch,
  onSearchChange: (patch: Partial<VoiceSearch>) => void,
  audience: VoiceAudience
) {
  const base = useVoiceRequestBase(search, onSearchChange);
  const stream = audience === 'sales' ? salesStreamRequest(base) : null;
  return {
    blocks: KINDS_OF_AUDIENCE[audience].map(
      (kind) => buildVoiceRequest(base, kind).queryKey
    ),
    stream: stream ? [stream.queryKey] : [],
  };
}

/**
 * Every answer of the themes block an organization has in the cache, whatever
 * the window, filter and cut. An edit of the theme set invalidates them: a
 * new, renamed or archived theme changes the block's rows, definitions and
 * each row's base — not only the set's panel.
 */
export function voiceThemeAnalyticsFilter(orgId: string | null): QueryFilters {
  return {
    queryKey: ['analytics', 'voice', orgId],
    // The kind is the LAST element of the key (`useVoiceRequest`): looking at the
    // other elements would match a project id or a category that starts with it.
    predicate: ({ queryKey }) => {
      const kind = queryKey[queryKey.length - 1];
      return typeof kind === 'string' && kind.startsWith(THEMES_KIND_PREFIX);
    },
  };
}

/**
 * Every answer of the section narrowed by a theme (`themeCode`) an organization
 * has in the cache. An edit of the theme set invalidates them too: after an
 * archive the screen must not keep that theme's numbers from the cache — the
 * server answers 400 now (spec §3).
 */
export function voiceThemeFilteredFilter(orgId: string | null): QueryFilters {
  return themeFilteredQueries(['analytics', 'voice', orgId], THEME_FROM_END);
}

/**
 * The conversation-themes block (`only=themes`, #845 PR 6), by the section's
 * demo rule (`useVoiceRequest`). The cut is the tab's: `outcomes` on Sales
 * (quality and declines), `questions` on Site and content (unanswered
 * questions) — one key each, so a tab switch never reads the other's cut.
 *
 * `enabled` is the flag `enableConversationThemes` as the client knows it: off
 * means no request at all, never a request whose answer is then hidden.
 *
 * No previous numbers under a new filter on either tab: the block shows its own
 * loading state. On Sales the filter bar belongs to the tab (`VoiceSalesTab`),
 * and on Site and content it follows this very query only for a background
 * refetch — a placeholder would show the old filter's numbers with no sign of it.
 */
export function useVoiceThemeAnalytics(
  search: VoiceSearch,
  onSearchChange: (patch: Partial<VoiceSearch>) => void,
  cut: VoiceThemeCuts | undefined,
  enabled: boolean,
  { keepPrevious = false }: { keepPrevious?: boolean } = {}
) {
  const request = useVoiceRequest(search, onSearchChange, themesKind(cut));
  return useQuery({
    queryKey: request.queryKey,
    queryFn: fetchThemes(request, cut),
    enabled: enabled && request.enabled,
    // Only for the theme picker's own observer: it keeps the last window's
    // options while a new one loads (never another organization's). The block
    // itself shows its loading state — no old numbers under a new filter.
    placeholderData: (previous, previousQuery) =>
      keepPrevious && keepsPlaceholder(previousQuery?.queryKey, request.queryKey)
        ? keepPreviousData(previous)
        : undefined,
    retry: retryTransientOnly,
  });
}
